import type {Node, Element, Parent} from 'hast';

import {BenchlingTagConfigs, BenchlingTagProperty, parseBnchFileJSON} from 'llm/markdown/LLMMarkdownUtils';

import {SaveToEntryOption} from './LLMMarkdownSection';

export interface RawNode extends Node {
  type: 'raw';
  value: string;
}

// Custom tree traversal function to visit raw nodes
const visitRawNodes = (
  node: Node,
  callback: (rawNode: RawNode, index: number, parent: Parent) => void
): void => {
  const parent = node as Parent;
  if (parent.children != null && Array.isArray(parent.children)) {
    // Process children in reverse order to handle array modifications safely
    for (let i = parent.children.length - 1; i >= 0; i--) {
      const child = parent.children[i];
      // The 'raw' type is not included in the standard hast Node type definition,
      // but it can exist in practice when processing markdown with raw HTML content
      // @ts-expect-error
      if (child.type === 'raw') {
        callback(child as RawNode, i, parent);
      } else {
        visitRawNodes(child, callback);
      }
    }
  }
};

export const rehypeBenchlingTags =
  (options: BenchlingTagConfigs = {tagConfigs: []}) =>
  (tree: Node): void => {
    visitRawNodes(tree, (node: RawNode, index: number, parent: Parent): void => {
      const rawValue = node.value;

      for (const config of options.tagConfigs) {
        const pattern = new RegExp(`^<${config.markdownTag}>([\\s\\S]*?)</${config.markdownTag}>$`);
        const match = rawValue.match(pattern);
        if (match) {
          const content = match[1];
          if (config.validator(content)) {
            const properties: Record<string, string> = {};
            if (config.properties.includes(BenchlingTagProperty.ID_VALUE)) {
              properties.idValue = config.extractIdValue ? config.extractIdValue(content) : content;
            }
            if (config.properties.includes(BenchlingTagProperty.LINK_URL)) {
              properties.linkUrl = content;
            }
            // For bnch_file tags, parse JSON and extract individual properties
            if (config.markdownTag === 'bnch_file') {
              const parsed = parseBnchFileJSON(content);
              if (parsed != null) {
                properties.fileId = parsed.fileId;
                properties.filename = parsed.filename;
                properties.mediaType = parsed.mediaType;
              }
            }
            const newNode: Element = {
              type: 'element',
              tagName: config.htmlTagName,
              properties,
              children: [], // We should not be adding children for raw nodes in remark
            };

            parent.children[index] = newNode;
            return;
          }
        }
      }
    });
  };

export const rehypeBenchlingTagsForSave =
  (options: BenchlingTagConfigs = {tagConfigs: []}) =>
  (tree: Node): void => {
    visitRawNodes(tree, (node: RawNode, index: number, parent: Parent): void => {
      const rawValue = node.value;

      for (const config of options.tagConfigs) {
        if (!config.markdownTag) {
          continue;
        }

        const pattern = new RegExp(`^<${config.markdownTag}>([\\s\\S]*?)</${config.markdownTag}>$`);
        const match = rawValue.match(pattern);

        if (match) {
          const content = match[1];
          switch (config.preserveContentWhenSaving) {
            case SaveToEntryOption.PRESERVE_TAGS: {
              if (config.validator(content)) {
                const properties: Record<string, string> = {};
                if (config.properties.includes(BenchlingTagProperty.ID_VALUE)) {
                  properties.idValue = config.extractIdValue ? config.extractIdValue(content) : content;
                }
                if (config.properties.includes(BenchlingTagProperty.LINK_URL)) {
                  properties.linkUrl = content;
                }
                // For bnch_file tags, parse JSON and extract individual properties
                if (config.markdownTag === 'bnch_file') {
                  const parsed = parseBnchFileJSON(content);
                  if (parsed != null) {
                    properties.fileId = parsed.fileId;
                    properties.filename = parsed.filename;
                    properties.mediaType = parsed.mediaType;
                  }
                }
                const newNode: Element = {
                  type: 'element',
                  tagName: config.htmlTagName,
                  properties,
                  children: [], // We should not be adding children for raw nodes in remark
                };

                parent.children[index] = newNode;
              }
              break;
            }
            case SaveToEntryOption.PRESERVE_TEXT: {
              node.value = content;
              break;
            }
            case SaveToEntryOption.REMOVE: {
              parent.children.splice(index, 1);
              break;
            }
            default:
              break;
          }

          return;
        }
      }
    });
  };
